import { useMemo, useState } from "react"
import { Plus, Trash2 } from "lucide-react"

import { ApiError } from "@/api/client"
import {
  useCreateTopUpRequests,
  useTopUpEligibleAccounts,
} from "@/api/hooks/requests"
import { SearchableSelect } from "@/components/SearchableSelect"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import { RequestSideDrawer } from "./RequestSideDrawer"

const TOP_UP_BATCH_MAX = 10

type TopUpRow = {
  key: string
  advertiserId: string
  amount: string
}

function newRow(): TopUpRow {
  return {
    key: crypto.randomUUID(),
    advertiserId: "",
    amount: "",
  }
}

function currencySymbol(code: string): string {
  const upper = code.toUpperCase()
  if (upper === "USD") return "$"
  if (upper === "EUR") return "€"
  if (upper === "GBP") return "£"
  return upper
}

type TopUpDrawerProps = {
  open: boolean
  onClose: () => void
  onSubmitted?: () => void
}

export function TopUpDrawer({ open, onClose, onSubmitted }: TopUpDrawerProps) {
  const accountsQuery = useTopUpEligibleAccounts(open)
  const createMutation = useCreateTopUpRequests()
  const [rows, setRows] = useState<TopUpRow[]>([newRow()])
  const [error, setError] = useState<string | null>(null)

  const accounts = useMemo(
    () => accountsQuery.data?.accounts ?? [],
    [accountsQuery.data],
  )
  const accountsById = useMemo(() => {
    const map = new Map(accounts.map((acc) => [acc.advertiser_id, acc]))
    return map
  }, [accounts])

  const selectedIds = useMemo(
    () => new Set(rows.map((r) => r.advertiserId).filter(Boolean)),
    [rows],
  )

  const accountOptions = useMemo(
    () =>
      accounts.map((acc) => ({
        value: acc.advertiser_id,
        label: `${acc.advertiser_name} (${acc.advertiser_id})`,
        searchText: `${acc.advertiser_name} ${acc.advertiser_id}`,
      })),
    [accounts],
  )

  const resetAndClose = () => {
    setRows([newRow()])
    setError(null)
    onClose()
  }

  const onSubmit = async () => {
    setError(null)
    const items: Array<{ advertiser_id: string; amount: number }> = []
    for (const row of rows) {
      if (!row.advertiserId) {
        setError("Select an account for every row")
        return
      }
      const amount = Number(row.amount)
      if (
        !Number.isFinite(amount) ||
        !Number.isInteger(amount) ||
        amount <= 0
      ) {
        setError("Enter a whole-dollar amount greater than 0 for every row")
        return
      }
      items.push({ advertiser_id: row.advertiserId, amount })
    }

    try {
      await createMutation.mutateAsync(items)
      onSubmitted?.()
      resetAndClose()
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to submit top-up request"
      setError(message)
    }
  }

  return (
    <RequestSideDrawer
      open={open}
      title="Request to Top Up an account"
      onClose={resetAndClose}
      footer={
        <div className="flex gap-3">
          <Button
            type="button"
            onClick={onSubmit}
            disabled={createMutation.isPending || accounts.length === 0}
            className="bg-[#3d8b5c] text-white hover:bg-[#337350]"
          >
            {createMutation.isPending ? "Submitting..." : "Submit"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={resetAndClose}
            className="bg-[#3a3a3a] text-white hover:bg-[#4a4a4a]"
          >
            Cancel
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error ? (
          <Alert
            variant="destructive"
            className="border-[#663333] bg-[#4a2a2a] text-[#f87171]"
          >
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {accountsQuery.isLoading ? (
          <p className="text-sm text-[#b0b0b0]">Loading accounts...</p>
        ) : accounts.length === 0 ? (
          <p className="text-sm text-[#f87171]">
            No assigned accounts available for top-up.
          </p>
        ) : null}

        <div>
          <h3 className="mb-1 text-sm font-semibold text-[#e0e0e0]">
            Accounts Top Up<span className="text-[#f87171]">*</span>
          </h3>
          <p className="mb-3 text-xs text-[#888]">
            Each account is submitted as a separate request (max {TOP_UP_BATCH_MAX}
            ).
          </p>
          <div className="space-y-3">
            {rows.map((row, index) => {
              const selected = accountsById.get(row.advertiserId)
              const currency = selected?.currency || "USD"
              const symbol = currencySymbol(currency)
              return (
                <div
                  key={row.key}
                  className="relative space-y-3 rounded-lg border border-[#4a4a4a] bg-[#2d2d2d] p-4"
                >
                  {rows.length > 1 ? (
                    <button
                      type="button"
                      aria-label="Remove row"
                      className="absolute right-3 top-3 text-[#f87171] hover:text-[#fca5a5]"
                      onClick={() =>
                        setRows((prev) => prev.filter((r) => r.key !== row.key))
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  ) : null}

                  <div className="space-y-1.5 pr-8">
                    <Label className="text-[#b0b0b0]">
                      Account<span className="text-[#f87171]">*</span>
                    </Label>
                    <SearchableSelect
                      value={row.advertiserId}
                      aria-label="Account"
                      placeholder="Select an option"
                      searchPlaceholder="Search by name or ID"
                      options={accountOptions.map((opt) => ({
                        ...opt,
                        disabled:
                          selectedIds.has(opt.value) &&
                          opt.value !== row.advertiserId,
                      }))}
                      onChange={(value) =>
                        setRows((prev) =>
                          prev.map((r) =>
                            r.key === row.key
                              ? { ...r, advertiserId: value }
                              : r,
                          ),
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-[#b0b0b0]">
                      Amount
                      {selected ? ` (${currency})` : ""}
                      <span className="text-[#f87171]">*</span>
                    </Label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#b0b0b0]">
                        {symbol}
                      </span>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        inputMode="numeric"
                        value={row.amount}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/[^\d]/g, "")
                          setRows((prev) =>
                            prev.map((r) =>
                              r.key === row.key
                                ? { ...r, amount: digits }
                                : r,
                            ),
                          )
                        }}
                        className="border-[#555] bg-[#3a3a3a] pl-10 text-white"
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <p className="text-xs text-[#888]">
                    Request {index + 1} of {rows.length}
                  </p>
                </div>
              )
            })}
          </div>

          <div className="mt-4 flex justify-center">
            <Button
              type="button"
              variant="outline"
              onClick={() => setRows((prev) => [...prev, newRow()])}
              disabled={
                accounts.length === 0 ||
                rows.length >= Math.min(TOP_UP_BATCH_MAX, accounts.length)
              }
              className="border-[#555] bg-transparent text-white hover:bg-[#3a3a3a]"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add another account
            </Button>
          </div>
        </div>
      </div>
    </RequestSideDrawer>
  )
}
